import React, { FC } from 'react';
import { ValidationRecord } from './types';
import { dateTimeFormat, timeZone } from '@lithos/DateUtils';
import ResultBadge from './ResultBadge';
import { FlexGroup, FlexItem, Flyout, Text, Tooltip, Icon, Code, KeyValueList, Link, useTectonTheme } from '@lithos';
import { useUserSettings } from '../../context/UserSettingsContext';

const ValidationsFlyout: FC<{
  validation: ValidationRecord;
  setFlyout: React.Dispatch<React.SetStateAction<React.ReactNode | undefined>>;
}> = ({ validation, setFlyout }) => {
  const { workspace } = useUserSettings();
  const { theme } = useTectonTheme();
  const items = [
    {
      title: 'Result',
      content: <ResultBadge result={validation.status} />,
    },
    {
      title: 'Feature View',
      content: (
        <Link
          url={`../${workspace}/features/${validation.featureViewName}`}
          icon="FeatureView"
          name={validation.featureViewName}
        />
      ),
    },
    {
      title: 'Validation Parameters',
      content: validation.expectationName,
    },
    {
      title: 'Details',
      content: validation.metricExpectationMetadata?.alertMessage,
    },
    {
      title: 'Completed At',
      content: dateTimeFormat(validation.validationTime, timeZone.UTC),
    },
  ];

  const inputItems =
    validation.metricExpectationMetadata?.inputMetrics?.map((metric) => {
      return {
        title: metric.metric,
        content: metric.value,
      };
    }) ?? [];

  const dataIntervalItems = [
    {
      title: 'Interval Start',
      content: dateTimeFormat(validation.featureIntervalStartTime, timeZone.UTC),
    },
    {
      title: 'Interval End',
      content: validation.featureIntervalEndTime
        ? dateTimeFormat(validation.featureIntervalEndTime, timeZone.UTC)
        : '-',
    },
  ];

  return (
    <Flyout
      title={<>Validation Details</>}
      isOpen={true}
      setIsOpen={() => {
        setFlyout(undefined);
      }}
      content={
        <FlexGroup direction="column" padding="0" gapSize="8">
          <FlexItem
            shrink={0}
            grow={1}
            css={{
              borderBottom: `1px solid ${theme.v1.colors.border.default}`,
              paddingBottom: `${theme.v1.size['4']}px`,
            }}
          >
            <FlexGroup direction="column" gapSize="2" padding="0">
              <Text element="h5">Metadata</Text>
              <KeyValueList items={items} />
            </FlexGroup>
          </FlexItem>
          <FlexItem
            css={{
              borderBottom: `1px solid ${theme.v1.colors.border.default}`,
              paddingBottom: `${theme.v1.size['4']}px`,
            }}
          >
            <FlexGroup direction="column" gapSize="4" padding="0">
              <Text element="h5">Expectation Expression</Text>
              <Code code={validation.metricExpectationMetadata?.expression.expression ?? ''} />
            </FlexGroup>
          </FlexItem>
          <FlexItem
            css={{
              borderBottom: `1px solid ${theme.v1.colors.border.default}`,
              paddingBottom: `${theme.v1.size['4']}px`,
            }}
          >
            <FlexGroup direction="column" gapSize="4" padding="0">
              <FlexGroup padding="0" gapSize="2">
                <Text element="h5">Input Parameters</Text>{' '}
                <Tooltip
                  trigger={<Icon type="mono" icon="QuestionInCircle" size="s" />}
                  content={`Parameters used as input to the expectation expression, from the data in the current interval.`}
                />
              </FlexGroup>
              <KeyValueList items={inputItems} />
            </FlexGroup>
          </FlexItem>
          <FlexItem>
            <FlexGroup direction="column" gapSize="4" padding="0">
              <Text element="h5">Data Interval</Text>
              <KeyValueList items={dataIntervalItems} />
            </FlexGroup>
          </FlexItem>
        </FlexGroup>
      }
    />
  );
};

export default ValidationsFlyout;
